import * as Sentry from '@sentry/nextjs'
import { toast } from 'react-toastify'

import { Address, UpdatedItem } from '@/models/Index'
import { API_URL } from '@/utils/Consts'
import { safeSessionStorage } from '@/utils/SafeStorage'
import { Action0, Action1, RequestBody } from '@/utils/Types'

import axios from './axios'

export const appUtils = {
  async fetcher(url: string, withCache?: boolean) {
    if (withCache) {
      const res = safeSessionStorage.getItem(url)
      if (res) {
        return JSON.parse(res)
      }
    }
    const res = await axios.get(url)
    if (res && res.data && res.data.code == '0') {
      const data = res.data.data
      if (withCache && data) {
        if (Array.isArray(data) && data.length == 0) {
          return data
        }
        safeSessionStorage.setItem(url, JSON.stringify(data))
      }
      return data
    }
    throw new Error('An error occurred while fetching the data.')
  },

  getErrorMsgByCode(code: string, errStr: {}, msg?: string) {
    if (errStr[code] !== undefined) {
      return errStr[code]
    } else {
      if (code == '200') return
      return msg && code > '500' ? msg : errStr['general_error']
    }
  },

  handleApiError(e, errStr: {}) {
    if (e?.response?.data?.code && e.response?.data?.code !== '1000') {
      const resCode: string = e.response.data.code
      const errorData = e.response.data.data
      const message = this.getErrorMsgByCode(resCode, errStr, e.response?.data?.message)
      const values = errorData ? Object.values(errorData) : []
      if (values.length === 0) {
        const stripped = message
          .replace(/[^.!?]*%d[^.!?]*[.!?]?/g, '')
          .replace(/\s+/g, ' ')
          .trim()
        return stripped || message
      }
      return message.templated(...values)
    } else {
      if (e?.isAxiosError && !e?.response) {
        return 'Network error, please try again.'
      }
      let fullErrorStr = ''
      try {
        fullErrorStr = JSON.stringify(e, Object.getOwnPropertyNames(e), 2) // 尝试序列化所有自身属性
      } catch (err) {
        fullErrorStr = `Failed to stringify error: ${err}`
      }
      axios.post(API_URL.logError, {
        message: e?.message,
        stack: e?.stack,
        name: e?.name,
        code: e?.response?.data?.code,
        data: e?.response?.data?.data,
        fullErrorStr: fullErrorStr,
        url: window.location.href,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        timestamp: new Date().toISOString(),
        // HTTP响应信息
        responseStatus: e?.response?.status,
        responseStatusText: e?.response?.statusText,
        responseHeaders: e?.response?.headers,
        responseData: e?.response?.data,
        // 请求信息
        requestUrl: e?.config?.url,
        requestMethod: e?.config?.method,
        requestParams: e?.config?.params,
        requestData: e?.config?.data,
        requestHeaders: e?.config?.headers,
        // 其他错误信息
        cause: e?.cause,
        isAxiosError: e?.isAxiosError,
        toJSON: typeof e?.toJSON === 'function' ? e.toJSON() : null,
      })
      return this.getErrorMsgByCode('general_error', errStr)
    }
  },

  /**
   * 非关键调用的兜底 .catch 统一走这里(RL-1308)。
   *
   * 只加 .catch 不上报,等于把真故障和噪音一起吞掉:
   * - 用户端网络问题(断网 / 中途切页 / 超时,即 axios 无 response)不是缺陷,静默即可,
   *   这本来就是 sentry.client.config.js 的 beforeSend 要丢掉的那一类;
   * - 会话状态(401 / 403 / 419)由 auth 层负责跳转,也不算缺陷;
   * - 其余(5xx、业务错误码、真实 JS 异常)必须上报,否则接口坏掉我们永远不知道。
   *
   * console 输出只在非生产环境:生产环境不向用户控制台暴露任何内容。
   */
  reportError(context: string, e: any) {
    const status = e?.response?.status
    const isUserNetworkError = e?._isUserNetworkError || (e?.isAxiosError && !e?.response)
    const isAuthState = status == 401 || status == 403 || status == 419
    if (process.env.NEXT_PUBLIC_ENVIRONMENT != 'production') {
      console.log(`[${context}]`, e?.message || e)
    }
    if (isUserNetworkError || isAuthState) return
    Sentry.captureException(e, { tags: { error_context: context } })
  },

  async postData(
    url: string,
    params: RequestBody,
    errStr: {},
    toastError?: boolean,
    setLoading?: Action1<boolean>,
    // eslint-disable-next-line no-unused-vars
    onError?: (msg: string, code?: string) => void
  ) {
    let errorMessage = ''
    let errorCode = null
    if (setLoading) {
      setLoading(true)
    }
    const res = await axios
      .post(url, params)
      .then(res => {
        if (setLoading) {
          setLoading(false)
        }
        if (res && res.data && res.data.code != '0') {
          errorCode = res.data.code
          errorMessage = this.getErrorMsgByCode(res.data.code, errStr)
          return null
        }
        return res.data.data
      })
      .catch(e => {
        if (setLoading) {
          setLoading(false)
        }
        errorCode = e.response?.data?.code
        errorMessage = this.handleApiError(e, errStr)
      })
    if (errorMessage) {
      if (toastError) {
        toast.error(errorMessage, { toastId: errorCode })
      }
      if (onError) {
        onError(errorMessage, errorCode != null ? String(errorCode) : undefined)
      }
      return null
    }
    return res
  },

  async postFormData(url: string, form: FormData, errStr: {}, onError?: Action1<string>) {
    let errorMessage = ''
    let errorCode = null
    const res = await axios
      .post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(res => {
        if (res && res.data && res.data.code != '0') {
          errorCode = res.data.code
          errorMessage = this.getErrorMsgByCode(res.data.code, errStr)
          return null
        }
        return res.data.data
      })
      .catch(e => {
        errorCode = e.response?.data?.code
        errorMessage = this.handleApiError(e, errStr)
      })
    if (errorMessage) {
      if (onError) onError(errorMessage)
      else toast.error(errorMessage, { toastId: errorCode })
      return null
    }
    return res
  },

  addressFormat(address: Address) {
    return (
      this.getString(address.street_2, ', ') +
      this.getString(address.street, ', ') +
      this.getString(address.county, ', ') +
      this.getString(address.city, ', ') +
      this.getString(address.postcode, ', ') +
      this.getString(address.country)
    )
  },

  getString(str: string, sufx?: string) {
    return str ? str + (sufx ?? '') : ''
  },

  getItemsStr(itemNumber: number, cartStr: {}) {
    const itemStrCode = itemNumber > 1 ? ' items' : ' item'
    if (cartStr[itemStrCode] !== undefined) {
      return itemNumber + cartStr[itemStrCode]
    }
    return itemNumber + itemStrCode
  },

  isEmpty(obj: any) {
    if (!obj || obj.length == 0) {
      return true
    }
    switch (obj) {
      case '':
      case 0:
      case '0':
      case null:
      case false:
      case typeof obj == 'undefined':
        return true
      default:
        return false
    }
  },
  getInventoryAlert(updatedItems: UpdatedItem[], errStr: {}) {
    return updatedItems
      .map(item => {
        const str = item.limit ? errStr['limited_per_user'].templated(item.limit) : errStr['stock_remaining'].templated(item.qty)
        return '"' + item.item_name + '"' + str
      })
      .join('\n')
  },

  getDefaultCurrency() {
    return 'GBP' // TODO 根据 域名 不同
  },
  isFakeEmail(email: string) {
    return email && (email.endsWith('google.com') || email.endsWith('facebook.com') || email.endsWith('twitter.com') || email.endsWith('twitteroauth2'))
  },

  evaluateRemoteScript(url: string, onLoad?: Action0 | null, beforeLoading?: Action0) {
    const script = document.createElement('script')
    script.src = url
    script.async = true
    if (onLoad) {
      script.onload = onLoad
    }
    if (beforeLoading) {
      beforeLoading()
    }
    document.body.appendChild(script)
  },
  getTopLevelDomain() {
    const hostname = window.location.hostname // 当前页面的主机名
    const parts = hostname.split('.')
    if (parts.length <= 2 || /^(localhost|[\d.]+)$/.test(hostname)) {
      return hostname
    }
    // 返回顶级域名（最后两部分，例如 example.com）
    return parts.slice(-2).join('.')
  },

  /**
   * 验证地址信息并显示相应的错误提示
   * @param address 地址对象
   * @param withToast 是否显示错误提示
   * @returns 验证是否通过
   */
  validateBillingAddress(address: any, errStr: any, withToast: boolean = false): boolean {
    if (!address) return false
    const requiredFields = [
      { field: 'first_name', message: errStr.billing_address_first_name_required },
      { field: 'last_name', message: errStr.billing_address_last_name_required },
      { field: 'email', message: errStr.billing_address_email_required },
      { field: 'phone', message: errStr.billing_address_phone_required },
      { field: 'street', message: errStr.billing_address_street_required },
      { field: 'city', message: errStr.billing_address_city_required },
      { field: 'country', message: errStr.billing_address_country_required },
    ]

    // Check postcode (required unless Republic of Ireland)
    if (address.country !== 'Republic of Ireland') {
      requiredFields.push({ field: 'postcode', message: errStr.billing_address_postcode_required })
    }

    for (const { field, message } of requiredFields) {
      if (!address[field] || address[field].toString().trim() === '') {
        if (withToast) {
          toast.error(message, { toastId: 'billing-address-required' })
        }
        return false
      }
    }
    return true
  },
}
